import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Package } from 'lucide-react';
import type { WizardFacturaFormData } from '../../schemas/facturaSchema';
import { capitalizarIngrediente, capitalizarMarca } from '../../utils/facturaFormatters';
import { formatearPrecio, formatearPrecioUnitario } from '../../utils/facturaCalculos';

interface WizardStep3Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<WizardFacturaFormData, any, any>;
  totales: {
    subtotal: number;
    iva: number;
    total: number;
  };
}

const WizardStep3Resumen: React.FC<WizardStep3Props> = ({ form, totales }) => {
  const formData = form.watch();

  return (
    <div className="space-y-4">
      {/* Datos de la factura */}
      <div
        className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800"
        role="region"
        aria-labelledby="resumen-datos-factura"
      >
        <h3
          id="resumen-datos-factura"
          className="font-medium mb-3 text-gray-900 dark:text-gray-100"
        >
          Datos de la Factura
        </h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Número:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
              {formData.numero_factura}
            </span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Fecha:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
              {formData.fecha_emision}
            </span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Proveedor:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
              {formData.nombre_proveedor}
            </span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">RUT:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
              {formData.rut_proveedor}
            </span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">
              Método de pago:
            </span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100 capitalize">
              {formData.metodo_pago}
            </span>
          </div>
          {formData.valores_incluyen_iva && (
            <div className="col-span-2">
              <span className="text-yellow-700 dark:text-yellow-400 text-xs font-medium">
                Los precios originales incluían IVA (convertidos automáticamente)
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tabla de ingredientes */}
      <div
        className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden"
        role="region"
        aria-labelledby="resumen-ingredientes"
      >
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                id="resumen-ingredientes"
                className="px-4 py-2 text-left text-gray-700 dark:text-gray-300"
              >
                Ingrediente
              </th>
              <th className="px-4 py-2 text-left text-gray-700 dark:text-gray-300">
                Marca
              </th>
              <th className="px-4 py-2 text-left text-gray-700 dark:text-gray-300">
                Categoría
              </th>
              <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                Cantidad
              </th>
              <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                Precio
              </th>
              <th className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                Subtotal
              </th>
            </tr>
          </thead>
          <tbody>
            {formData.detalles.map((detalle, index) => {
              const tieneJerarquia = ((detalle as any).jerarquia_niveles ?? 0) >= 2;
              const descripcionJerarquia = (detalle as any).descripcion_jerarquia as string | null | undefined;
              const precioNivel1 = (detalle as any).precio_nivel1 as number | null | undefined;
              const precioNivel2 = (detalle as any).precio_nivel2 as number | null | undefined;
              const nivel1Nombre = (detalle as any).nivel1_nombre as string | null | undefined;
              const nivel2Nombre = (detalle as any).nivel2_nombre as string | null | undefined;
              return (
                <tr
                  key={index}
                  className="border-t border-gray-200 dark:border-gray-700"
                >
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 dark:text-gray-100">
                        {capitalizarIngrediente(detalle.nombre_ingrediente_temporal)}
                      </span>
                      {tieneJerarquia && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium
                                     bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                        >
                          <Package className="w-3 h-3" />
                          Grupo
                        </span>
                      )}
                      {!tieneJerarquia && detalle.es_pack && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium
                                     bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                          title={`Pack: ${detalle.cantidad_packs}×${detalle.unidades_por_pack}`}
                        >
                          <Package className="w-3 h-3" />
                          {detalle.cantidad_packs}×{detalle.unidades_por_pack}
                        </span>
                      )}
                    </div>
                    {tieneJerarquia && descripcionJerarquia && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        {descripcionJerarquia}
                      </div>
                    )}
                    {!tieneJerarquia && detalle.es_pack && detalle.formato_compra && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {detalle.formato_compra}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                    {capitalizarMarca(detalle.marca)}
                  </td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                    {detalle.categoria_nombre || 'Otros'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="text-gray-900 dark:text-gray-100">
                      {detalle.cantidad} {detalle.unidad_medida}
                    </div>
                    {(tieneJerarquia || detalle.es_pack) && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        (Total calculado)
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="text-gray-900 dark:text-gray-100">
                      ${formatearPrecioUnitario(detalle.precio_unitario)}/{detalle.unidad_medida}
                    </div>
                    {tieneJerarquia && precioNivel1 != null && nivel1Nombre && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        ${formatearPrecioUnitario(precioNivel1)}/{nivel1Nombre}
                      </div>
                    )}
                    {tieneJerarquia && precioNivel2 != null && nivel2Nombre && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        ${formatearPrecioUnitario(precioNivel2)}/{nivel2Nombre}
                      </div>
                    )}
                    {!tieneJerarquia && detalle.es_pack && detalle.precio_pack && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Pack: ${formatearPrecioUnitario(detalle.precio_pack)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">
                    ${formatearPrecio(detalle.subtotal)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 dark:bg-gray-800 font-medium">
            <tr className="border-t border-gray-200 dark:border-gray-700">
              <td
                colSpan={5}
                className="px-4 py-2 text-right text-gray-700 dark:text-gray-300"
              >
                Subtotal (sin IVA):
              </td>
              <td className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">
                ${formatearPrecio(totales.subtotal)}
              </td>
            </tr>
            <tr className="border-t border-gray-200 dark:border-gray-700">
              <td
                colSpan={5}
                className="px-4 py-2 text-right text-gray-700 dark:text-gray-300"
              >
                IVA (19%):
              </td>
              <td className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">
                ${formatearPrecio(totales.iva)}
              </td>
            </tr>
            <tr className="text-lg border-t border-gray-200 dark:border-gray-700">
              <td
                colSpan={5}
                className="px-4 py-2 text-right text-gray-700 dark:text-gray-300"
              >
                Total:
              </td>
              <td className="px-4 py-2 text-right text-blue-600 dark:text-blue-400">
                ${formatearPrecio(totales.total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Observaciones si existen */}
      {formData.observaciones && (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
          <h3 className="font-medium mb-2 text-gray-900 dark:text-gray-100">
            Observaciones
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {formData.observaciones}
          </p>
        </div>
      )}
    </div>
  );
};

export default WizardStep3Resumen;
